import iconEmpty from '@design/../static/error-empty.svg';
import { Button, ButtonProps } from '@design/components/button/Button';
import { Icon } from '@design/components/icon/Icon';
import clsx from 'clsx';
import React, { ReactNode } from 'react';

import styles from './ErrorMessage.module.scss';

export interface ErrorButton extends Partial<ButtonProps> {
  /** Visible button text. */
  label: string;
  /** Click handler for the action button. */
  onClick?: () => void;
}

export interface ErrorMessageProps {
  /** Extra class forwarded to the wrapper. */
  className?: string;
  /**
   * Decorative icon at the top:
   * `'error'` shows a yellow `ExclamationTriangle`,
   * `'empty'` shows the design-system empty-state illustration,
   * omit for a text-only message.
   */
  displayIcon?: 'error' | 'empty';
  /** Heading text. */
  title?: ReactNode;
  /** Body text rendered under the title. */
  subtitle?: ReactNode;
  /** Optional action button (e.g. "Retry"). Rendered as a primary, small `Button` by default — `buttonProps` override the defaults. */
  buttonProps?: ErrorButton;
}

const EmptyIcon = iconEmpty as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;

/**
 * Empty / error state placeholder that fills a widget or panel when
 * there's no data, an error occurred, or the user needs to take an
 * action to proceed.
 *
 * Includes an optional icon (error triangle or empty-state
 * illustration), title, subtitle, and a single action button.
 */
export const ErrorMessage = ({
  className,
  displayIcon,
  title,
  subtitle,
  buttonProps,
}: ErrorMessageProps) => {
  return (
    <div
      className={clsx(styles.wrapper, className)}
      data-testid="widget-error-state"
      data-error-message
    >
      {displayIcon === 'error' && (
        <Icon
          className={styles.errorIcon}
          data-testid="error-icon"
          iconName="ExclamationTriangle"
          size={60}
          viewBox="0 0 60 60"
        />
      )}

      {displayIcon === 'empty' && (
        <EmptyIcon className={styles.emptyIcon} data-testid="empty-icon" />
      )}

      {title && <div className={styles.title}>{title}</div>}
      {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
      <div>
        {buttonProps && (
          <Button
            onClick={buttonProps?.onClick}
            variant="primary"
            size="small"
            {...buttonProps}
          />
        )}
      </div>
    </div>
  );
};
